// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import ActionBar from '../layout/ActionBar';
import RequestsToBeApprovedList from './components/RequestsToBeApprovedList';
import RequestKeys from './slideout/RequestKeys';
import RequestTransactionWarning from './slideout/RequestTransactionWarning';
import { userIs } from '../auth/authUtils';
import userTypes from '../../constants/userTypes';
import SlidingPane from 'react-sliding-pane';
import TableViewSwitcher from './components/TableViewSwitcher';
import RequestsToBeApprovedGroupedList from './components/RequestsToBeApprovedGroupedList';
import Button from '../common/Button';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../../util/pageTitle';
import {
  approveRequest,
  checkRequestTransactionLimits,
  declineRequest,
} from '../../api/requestApi';
import { useAppSelector } from '../../hooks';
import { toast } from 'react-toastify';

export interface RequestsToBeApprovedProps {
  setCompleteRequestSlideoutOpen: (open: boolean) => void;
}

function RequestsToBeApproved({ setCompleteRequestSlideoutOpen }: RequestsToBeApprovedProps) {
  usePageTitle(['Approve requests']);
  const { t } = useTranslation();
  const currentUser = useAppSelector((state) => state.auth);
  const [requestsGrouped, setRequestsGrouped] = useLocalStorage(
    'requestsGrouped-approve',
    false
  );
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const groupedParam = searchParams.get('grouped');
    if (groupedParam === 'true') {
      setRequestsGrouped(true);
    } else if (groupedParam === 'false') {
      setRequestsGrouped(false);
    }
  }, [searchParams, setRequestsGrouped]);

  useEffect(() => {
    if (searchParams.get('grouped') === null) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('grouped', String(requestsGrouped));
          return next;
        },
        { replace: true }
      );
    }
  }, [searchParams, requestsGrouped, setSearchParams]);

  const handleSetRequestsGrouped = (value: boolean) => {
    setRequestsGrouped(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('grouped', String(value));
        return next;
      },
      { replace: true }
    );
  };

  const [reload, setReload] = useState<Date | undefined>();
  const [requestKeysSlideoutOpen, setRequestKeysSlideoutOpen] = useState(false);
  const [
    requestTransactionWarningSlideoutOpen,
    setRequestTransactionWarningSlideoutOpen,
  ] = useState(false);
  const [
    requestTransactionWarningSlideoutData,
    setRequestTransactionWarningSlideoutData,
  ] = useState<{
    requestId: string;
    signature: string;
    overrideRequestValue?: boolean;
    onDone: (ok: boolean) => void;
    d: any;
  } | undefined>();

  const handleApproveRequest = (
    requestId: string,
    signature: string,
    overrideRequestValue: boolean | undefined,
    onDone: (ok: boolean) => void
  ) => {
    checkRequestTransactionLimits(requestId, overrideRequestValue).then((d) => {
      if (d.isOverTransactionLimit || d.isOverGlobalTransactionLimit) {
        setRequestTransactionWarningSlideoutData({
          requestId,
          signature,
          overrideRequestValue,
          onDone,
          d,
        });
        setRequestTransactionWarningSlideoutOpen(true);
      } else {
        approveRequest(requestId, signature, overrideRequestValue)
          .then((r) => {
            if (r?.warningMessages) {
              r.warningMessages.forEach((message: string) =>
                toast.warn(`${t('Warning')}: ${message}`)
              );
            }
          })
          .catch((e) => {
            onDone(false);
            console.error(e);
            toast.error(e.message);
          });
        onDone(true);
      }
    });
  };

  const handleDeclineRequest = (
    requestId: string,
    declineReason: string,
    onDone: (ok: boolean) => void
  ) => {
    declineRequest(requestId, declineReason)
      .then((r) => {
        if (r?.warningMessages) {
          r.warningMessages.forEach((message: string) =>
            toast.warn(`${t('Warning')}: ${message}`)
          );
        }
        onDone(true);
      })
      .catch((e) => {
        onDone(false);
        console.error(e);
        toast.error(e.message);
      });
  };

  return (
    <>
      <ActionBar>
        {userIs(currentUser, userTypes.REQUESTER) && (
          <Button
            className="btn btn-primary"
            onClick={() => setRequestKeysSlideoutOpen(true)}
            text={t('Request keys')}
          />
        )}
      </ActionBar>
      {requestsGrouped ? (
        <RequestsToBeApprovedGroupedList
          reload={reload}
          onApproveRequest={handleApproveRequest}
          onDeclineRequest={handleDeclineRequest}
          tableViewSwitcher={
            <TableViewSwitcher
              requestsGrouped={requestsGrouped}
              setRequestsGrouped={handleSetRequestsGrouped}
            />
          }
        />
      ) : (
        <RequestsToBeApprovedList
          reload={reload}
          onApproveRequest={handleApproveRequest}
          onDeclineRequest={handleDeclineRequest}
          tableViewSwitcher={
            <TableViewSwitcher
              requestsGrouped={requestsGrouped}
              setRequestsGrouped={handleSetRequestsGrouped}
            />
          }
        />
      )}

      <SlidingPane
        isOpen={requestKeysSlideoutOpen}
        hideHeader={true}
        from="right"
        className="small-side-panel"
        onRequestClose={() => setRequestKeysSlideoutOpen(false)}
      >
        <RequestKeys
          done={() => {
            setRequestKeysSlideoutOpen(false);
            setCompleteRequestSlideoutOpen(true);
            setReload(new Date());
          }}
          cancel={() => setRequestKeysSlideoutOpen(false)}
        />
      </SlidingPane>

      <SlidingPane
        isOpen={requestTransactionWarningSlideoutOpen}
        hideHeader={true}
        from="right"
        className="small-side-panel"
        onRequestClose={() => setRequestTransactionWarningSlideoutOpen(false)}
      >
        {requestTransactionWarningSlideoutData && (
          <RequestTransactionWarning
            done={() => {
              approveRequest(
                requestTransactionWarningSlideoutData.requestId,
                requestTransactionWarningSlideoutData.signature,
                requestTransactionWarningSlideoutData.overrideRequestValue
              )
                .then((r) => {
                  if (r?.warningMessages) {
                    r.warningMessages.forEach((message: string) =>
                      toast.warn(`${t('Warning')}: ${message}`)
                    );
                  }
                  setRequestTransactionWarningSlideoutOpen(false);
                  requestTransactionWarningSlideoutData.onDone(true);
                })
                .catch((e) => {
                  setRequestTransactionWarningSlideoutOpen(false);
                  requestTransactionWarningSlideoutData.onDone(false);
                  console.error(e);
                  toast.error(e.message);
                });
            }}
            cancel={() => {
              setRequestTransactionWarningSlideoutOpen(false);
              requestTransactionWarningSlideoutData.onDone(false);
            }}
            transactionLimits={requestTransactionWarningSlideoutData.d}
          />
        )}
      </SlidingPane>
    </>
  );
}

export default RequestsToBeApproved;
